/**
 * useInstallPrompt · maneja la instalabilidad de la PWA.
 *
 * Estados:
 *   - 'installed'    → ya corre en modo standalone (o se instaló esta sesión).
 *                      El botón "Instalar" se oculta.
 *   - 'installable'  → Chrome/Edge/Android disparó `beforeinstallprompt`.
 *                      Instalación en 1 clic vía promptInstall().
 *   - 'ios'          → Safari iOS (no soporta beforeinstallprompt). Se muestra
 *                      guía manual (Compartir → Agregar a inicio).
 *   - 'unavailable'  → ni instalable ni iOS (ej. ya instalada en otro contexto,
 *                      o browser sin soporte). El botón se oculta.
 */
import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState = 'unavailable' | 'installable' | 'ios' | 'installed';

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari standalone flag (no estándar, fuera de los tipos del DOM).
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ se reporta como Mac con touch.
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOSDevice || isIPadOS;
}

interface UseInstallPromptResult {
  state: InstallState;
  isIOS: boolean;
  /** Dispara el prompt nativo (Chrome/Edge/Android). Devuelve true si aceptó. */
  promptInstall: () => Promise<boolean>;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => detectStandalone());
  const isIOS = detectIOS();

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Evita el mini-infobar default del browser; lo disparamos nosotros.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    // Si el modo display cambia a standalone en vivo (instalación), reflejarlo.
    const mql = window.matchMedia('(display-mode: standalone)');
    const onDisplayChange = (e: MediaQueryListEvent) => {
      if (e.matches) setInstalled(true);
    };
    mql.addEventListener('change', onDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      mql.removeEventListener('change', onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === 'accepted';
  }, [deferred]);

  const state: InstallState = installed
    ? 'installed'
    : deferred
      ? 'installable'
      : isIOS
        ? 'ios'
        : 'unavailable';

  return { state, isIOS, promptInstall };
}
